"use client"

import { useMemo } from "react"
import { useUbPDPState } from "@/store/ubPDPState"
import { useTranslations } from "next-intl"

import { formatDateLegacy, formatPrice, round } from "@/lib/helper"
import { getCountry } from "@/lib/locale/locale-common-utils"
import {
  createDiscountDisplayOptions,
  determineCardPrices,
  generateBasePriceInfo,
  generateMinOrderInfoFromTranslations,
  generatePackagingInfoFromTranslations,
  generateStepOrderInfoFromTranslations,
} from "@/lib/price-utils"
import { PdpContext } from "@/lib/storefront/product/pdp-service"
import { determineEffectiveProductFromPdpContext, showProductPrice } from "@/lib/storefront/product/productHelper"
import { usePdpDynamicContext } from "@/components/product/pdp/usePdpDynamicContext"

const CATEGORY_DISCOUNT_PRICE_TOLERANCE = 0.01

export default function ProductDetailBuyboxPrice(props: {
  pdpContext: PdpContext
  showEcoTaxFlag: boolean | undefined
}) {
  const t = useTranslations("storefront")
  const pdpContext = usePdpDynamicContext(props.pdpContext)
  const productOrArticle = useMemo(() => determineEffectiveProductFromPdpContext(pdpContext), [pdpContext])
  const ecoTax: number | undefined = productOrArticle?.ecoTax
  const ecoTaxFormatted = formatPrice(pdpContext.isoLocale, ecoTax, productOrArticle?.currency)
  const minOrderQuantity = productOrArticle?.minOrderQuantity
  const stepOrderQuantity = productOrArticle?.stepOrderQuantity
  const showEcoTax =
    !!props.showEcoTaxFlag && getCountry(pdpContext.isoLocale) === "fr" && ecoTax !== undefined && ecoTax > 0
  const packagingUnitInfo = generatePackagingInfoFromTranslations(t, productOrArticle)
  const minOrderInfo = generateMinOrderInfoFromTranslations(t, productOrArticle)
  const stepOrderInfo = generateStepOrderInfoFromTranslations(t, productOrArticle)
  const categoryDiscount = productOrArticle?.categoryDiscount

  const minOrderQuantityVisible = minOrderQuantity && minOrderQuantity > 1 && minOrderInfo !== ""
  const stepOrderQuantityVisible = stepOrderQuantity && stepOrderQuantity > 1 && stepOrderInfo !== ""

  const { buyboxQuantity } = useUbPDPState()

  const cardPrices = useMemo(() => {
    return determineCardPrices(
      pdpContext.isoLocale,
      pdpContext.isB2B,
      pdpContext.prices,
      productOrArticle?.ecoTax,
      buyboxQuantity,
      productOrArticle?.id,
      createDiscountDisplayOptions(
        productOrArticle,
        pdpContext.userData?.generalDiscount,
        pdpContext.customerCategoryDiscount
      )
    )
  }, [
    pdpContext.isoLocale,
    pdpContext.isB2B,
    pdpContext.prices,
    productOrArticle?.ecoTax,
    productOrArticle?.id,
    productOrArticle?.categoryDiscount?.rate,
    pdpContext.userData?.generalDiscount,
    pdpContext.customerCategoryDiscount,
    buyboxQuantity,
  ])

  const showPrice = useMemo(() => {
    const { showPrice } = showProductPrice(productOrArticle, cardPrices)
    return showPrice
  }, [productOrArticle, cardPrices])

  const isReducedPrice = useMemo(() => {
    return cardPrices.listPrice > cardPrices.salePrice
  }, [cardPrices])

  const pricePerUnitText = useMemo(() => {
    return generateBasePriceInfo(pdpContext.isoLocale, productOrArticle, cardPrices?.salePrice)
  }, [pdpContext.isoLocale, productOrArticle, cardPrices])

  const discountApplied = useMemo(() => {
    const expectedSalePrice = categoryDiscount
      ? (1 - categoryDiscount.rate / 100) * cardPrices.listPrice
      : undefined
    const priceDifference =
      expectedSalePrice !== undefined ? Math.abs(expectedSalePrice - cardPrices.salePrice) : undefined
    const result =
      priceDifference !== undefined &&
      Number.isFinite(priceDifference) &&
      priceDifference <= CATEGORY_DISCOUNT_PRICE_TOLERANCE

    return result
  }, [categoryDiscount, cardPrices, pdpContext.isB2B, pdpContext.pricesLoading, productOrArticle?.id])

  const priceText = pdpContext.isB2B ? t("product.price_net") : t("product.price_gross")

  // AT-specific tax text keys: create these in messages if needed
  // product.tax_hint_at, product.tax_hint_gross_at, product.tax_hint_eco_at, product.tax_hint_eco_gross_at
  const isAT = getCountry(pdpContext.isoLocale) === "at"
  const taxText = isAT
    ? pdpContext.isB2B
      ? t("product.tax_hint_at")
      : t("product.tax_hint_gross_at")
    : pdpContext.isB2B
      ? t("product.tax_hint")
      : t("product.tax_hint_gross")
  const ecoTaxText = isAT
    ? pdpContext.isB2B
      ? t("product.tax_hint_eco_at", { ecoTax: ecoTaxFormatted })
      : t("product.tax_hint_eco_gross_at", { ecoTax: ecoTaxFormatted })
    : pdpContext.isB2B
      ? t("product.tax_hint_eco", { ecoTax: ecoTaxFormatted })
      : t("product.tax_hint_eco_gross", { ecoTax: ecoTaxFormatted })

  return (
    <div className={"flex flex-col items-start justify-start"}>
      {pdpContext.pricesLoading ? (
        <div className="flex min-h-28 flex-col items-start justify-start gap-3" aria-hidden="true">
          <div className="h-4 w-28 animate-pulse rounded-sm bg-gray-200" />
          <div className="h-7 w-40 animate-pulse rounded-sm bg-gray-200" />
          <div className="h-3 w-48 animate-pulse rounded-sm bg-gray-200" />
        </div>
      ) : showPrice ? (
        <div>
          {isReducedPrice ? (
            pdpContext.isB2B ? (
              <div className="flex flex-row items-baseline justify-start gap-1">
                {!cardPrices.isSinglePrice && (
                  /*B2B "ab" Listenpreis*/
                  <p className="text-sm text-nowrap text-gray-500">{t("product.price_from")}</p>
                )}
                {cardPrices.listPriceFormatted !== cardPrices.salePriceFormatted && (
                  /*B2B Streich Listenpreis*/
                  <p className="text-sm font-bold text-gray-500 line-through">{cardPrices.listPriceFormatted}</p>
                )}
              </div>
            ) : (
              /*B2C "Aktionspreis"*/
              <p className={"text-xs font-bold text-nowrap text-primary"}>{t("basket.add_dialog.special_offer")}</p>
            )
          ) : (
            pdpContext.isB2B && (
              /*B2B Strike-Price now hidden*/
              <div className="flex flex-row items-baseline justify-start gap-1">
                <p className="hidden text-sm font-bold line-through">-</p>
              </div>
            )
          )}

          <div className="flex gap-2">
            <div className="flex flex-row items-baseline justify-start gap-1">
              {!cardPrices.isSinglePrice && (
                <p className="text-sm text-nowrap text-gray-500">{t("product.price_from")}</p>
              )}
              <p className="text-xl font-bold text-primary">
                {/*"salePrice "*/}

                {cardPrices.salePriceFormatted}
                {/*{isB2BCustomer ? "*" : ""}*/}
              </p>
            </div>
            {packagingUnitInfo !== "" && (
              <p
                /* Preis pro Paar (Paar = 2 Stück) */
                className="pt-1 text-xs text-gray2"
              >
                {packagingUnitInfo}
              </p>
            )}
          </div>

          {showEcoTax ? (
            /*"showEcoTax in FR "*/
            <p className="pt-1 text-xs text-nowrap text-gray2">{ecoTaxText}</p>
          ) : (
            /*"showVat only else "*/
            <p className="pt-1 text-xs text-nowrap text-gray2">{taxText}</p>
          )}

          {(pricePerUnitText !== "" || minOrderQuantityVisible || stepOrderQuantityVisible) && (
            <>
              <div className="mt-2 flex flex-row gap-2">
                {pricePerUnitText !== "" && (
                  /*pricePerUnitText 17,33€ pro 1 LTR "*/
                  <p
                    className="text-xs text-nowrap text-gray-400"
                    dangerouslySetInnerHTML={{ __html: pricePerUnitText }}
                  />
                )}
                {minOrderQuantityVisible && (
                  <p className="text-xs text-nowrap text-gray-400">
                    {/*Mindestbestellmenge: 10 Stück "*/}
                    {minOrderInfo}
                  </p>
                )}
              </div>
              <div>
                {stepOrderQuantityVisible && (
                  <p className="text-xs text-nowrap text-gray-400">
                    {/*Mengeninterval: 2 "*/}
                    {stepOrderInfo}
                  </p>
                )}
              </div>
            </>
          )}
          {discountApplied && categoryDiscount && categoryDiscount.daysLeft >= 0 && categoryDiscount.daysLeft <= 6 && (
            <div className="text-sm text-red-700">
              {t("product.discount_price_hint", { end: formatDateLegacy(categoryDiscount.end) })}
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Preis auf Anfrage */}
          <p className={"text-xl font-bold text-nowrap text-primary"}>{t("product.ask_price")}</p>
        </div>
      )}
    </div>
  )
}
